import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';

import { RemoveConflictedEntries, removeConflictedEntries, ResolveByReplaceEntries } from '../../../actions/conflict';
import { InsertInQueueForDelete, insertOneEntryInQueueForDelete } from '../../../actions/entries';
import { SubmissionState } from '../../../constants';
import { selectIsConflictedEntry } from '../../../selectors/conflict';
import {
  selectAnyHasLockedState,
  selectBets,
  selectIsLockedEntry,
  selectRequestErrorShowing,
  selectSomeHasError,
} from '../../../selectors/entries';
import { selectCurrentStakeType } from '../../../selectors/stakeTypes';
import { selectConflictState } from '../../../selectors/state';
import { selectSubmission } from '../../../selectors/submission';
import { ConflictState } from '../../../types/conflict';
import { isReplaceAction } from '../../../utils/conflict';
import Name from '../Name';
import RemoveButton from '../RemoveButton';

import dictionary from './Header.dictionary.json';

import styles from '../EntryComponents.module.scss';

// ---

type StoreProps = {
  name?: string;
  showRequestError: boolean;
  disabled: boolean;
  isSingle: boolean;
  conflict: ConflictState;
  isConflictedEntry: boolean;
  isLockedEntry: boolean;
  hasEntriesErrors: boolean;
  price?: number | null;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<InsertInQueueForDelete | RemoveConflictedEntries>;
};

type EntryHeaderProps = {
  betId: number;
  className?: string;
  'data-test'?: string;
};

const EntryHeader: FC<EntryHeaderProps & DispatchProp & StoreProps> = ({
  betId,
  name,
  showRequestError,
  disabled,
  isSingle,
  conflict,
  isConflictedEntry,
  isLockedEntry,
  hasEntriesErrors,
  className,
  dispatch,
  price,
  ...props
}) => {
  const { t } = useTranslation();
  const hasConflict = conflict !== null;

  const hasReplaceAction = useMemo(() => (
    conflict?.actions.find((action) => (
      isReplaceAction(action)
    )) as (ResolveByReplaceEntries | undefined)
  ), [conflict?.actions]);

  const removeEntryHandler = useCallback(() => {
    if (hasConflict) {
      dispatch(removeConflictedEntries([betId]));
    }
    else {
      dispatch(insertOneEntryInQueueForDelete(betId));
    }
  }, [betId, hasConflict]);

  const dataGaAttributes = {
    ['data-ga-event']: 'cart-removeEvent',
    ['data-ga-event-content']: betId,
    ['data-ga-event-value']: price,
  };

  const renderRemoveButton = () => {
    if (
      (hasEntriesErrors && (!showRequestError && !isLockedEntry))
      || (disabled && !isSingle)
      || (hasConflict && !isConflictedEntry)
      || (hasConflict && !hasReplaceAction)
    ) {
      return;
    }

    return (
      <RemoveButton disabled={disabled}
                    data-test="entryHeader-removeButton"
                    {...dataGaAttributes}
                    onClick={removeEntryHandler} />
    );
  };

  return (
    <header className={classNames(styles.header, className)}
            data-test={classNames(props['data-test'], 'entryHeader-container')}>
      <Name value={name || t(dictionary.nameValueText, { betId })}
            data-test="entryHeader-entryName"
            className={styles.entryName}
            error={showRequestError || (isLockedEntry && !disabled)} />

      {renderRemoveButton()}
    </header>
  );
};

const mapStateToProps = (state: ReduxState, { betId }: EntryHeaderProps): StoreProps => {
  const entry = selectBets(state).get(betId);

  return {
    price: entry?.price,
    name: entry?.entity.names.get('long'),
    showRequestError: selectRequestErrorShowing(state, betId),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    isSingle: selectCurrentStakeType(state).has(StakeType.SINGLE),
    conflict: selectConflictState(state),
    isConflictedEntry: selectIsConflictedEntry(state, betId),
    isLockedEntry: selectIsLockedEntry(state, betId),
    hasEntriesErrors: selectSomeHasError(state) || selectAnyHasLockedState(state),
  };
};

export default connect(mapStateToProps)(EntryHeader);
